/**
 * HomePage - Main Live Rooms Feed
 * Vertical swipe navigation between artist rooms
 */

import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, ChevronDown, Settings2, Layout, Layers, Focus, ListMusic } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { useSnippetPlayer } from '../hooks/useSnippetPlayer';
import LiveRoom from '../components/LiveRoom';
import TopNav from '../components/TopNav';
import SignupPrompt from '../components/SignupPrompt';
import SnippetOverlay from '../components/SnippetOverlay';
import RoleSelectionModal from '../components/RoleSelectionModal';
import { StoryViewer, StoryCreator } from '../components/Stories';
import PlaylistsDrawer from '../components/PlaylistsDrawer';
import SleekHome from '../components/SleekHome';
import ClubRoom from '../components/ClubRoom';
import { cn } from '../utils/helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Genre configurations
const GENRES = [
  { id: 'all', name: 'All', color: '#00F0FF' },
  { id: 'hip-hop', name: 'Hip-Hop', color: '#FF007F' },
  { id: 'rnb', name: 'R&B', color: '#9333EA' },
  { id: 'gospel-hip-hop', name: 'Gospel Hip Hop', color: '#FFD700' },
  { id: 'gospel', name: 'Gospel', color: '#38BDF8' },
  { id: 'afrobeats', name: 'Afrobeats', color: '#39FF14' },
  { id: 'country', name: 'Country', color: '#F59E0B' },
  { id: 'pop', name: 'Pop', color: '#F472B6' },
  { id: 'reggae-dancehall', name: 'Reggae/Dancehall', color: '#22C55E' },
];

const HomePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, patchUser } = useAuth();
  const { currentTrack, isPlaying, playTrack, togglePlayPause, setRepeatMode, queueEnded, setQueueEnded } = useAudioPlayer();
  
  // Snippet player for non-authenticated users
  const {
    isSnippetMode,
    snippetTimeLeft,
    showSignupPrompt,
    currentSnippetTrack,
    playSnippet,
    stopSnippet,
    dismissSignupPrompt,
    SNIPPET_DURATION
  } = useSnippetPlayer(isAuthenticated);
  
  // State
  const [rooms, setRooms] = useState([]);
  const [stationTracks, setStationTracks] = useState([]);
  const [currentRoomIndex, setCurrentRoomIndex] = useState(0);
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [loading, setLoading] = useState(true);
  const [listeners, setListeners] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [energyLevel, setEnergyLevel] = useState(50);
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [showStoryViewer, setShowStoryViewer] = useState(null);
  const [showStoryCreator, setShowStoryCreator] = useState(false);
  const [showInterfaceMenu, setShowInterfaceMenu] = useState(false);
  const [showPlaylists, setShowPlaylists] = useState(false);
  const [interfaceMode, setInterfaceMode] = useState(() => {
    return localStorage.getItem('nltradio_interface') || 'classic';
  });
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('nlt_player_mode') || 'sleek';
  });
  const changeViewMode = (m) => {
    setViewMode(m);
    localStorage.setItem('nlt_player_mode', m);
  };
  // Measure the fixed TopNav height so the toggle + Discover panel never overlap it
  const [navHeight, setNavHeight] = useState(0);
  useLayoutEffect(() => {
    const el = document.querySelector('[data-testid="top-nav"]');
    if (!el) return;
    const update = () => setNavHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, [viewMode]);
  
  const lastScrollTime = useRef(Date.now());

  // Interface mode options
  const INTERFACE_MODES = [
    { id: 'classic', name: 'Classic', icon: Layout, desc: 'Full experience with all features' },
    { id: 'feed', name: 'Feed', icon: Layers, desc: 'Stream-focused minimal view' },
    { id: 'focus', name: 'Focus', icon: Focus, desc: 'Clean player, no distractions' },
  ];

  // Save interface preference
  const handleInterfaceChange = (mode) => {
    setInterfaceMode(mode);
    localStorage.setItem('nltradio_interface', mode);
    setShowInterfaceMenu(false);
  };

  // Check if new user needs to select role
  useEffect(() => {
    const checkRoleSelection = async () => {
      if (!isAuthenticated || !user) return;

      // Source of truth is the backend `role_selected` flag only.
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/artists/check-role`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!response.data?.has_profile) {
          // User has not explicitly picked a role yet — prompt them.
          setShowRoleSelection(true);
        }
      } catch (err) {
        console.log('Could not check role:', err);
      }
    };

    checkRoleSelection();
  }, [isAuthenticated, user]);

  const handleRoleSelect = async (role) => {
    if (!user) return;

    // Persist the choice server-side (sets role_selected) for BOTH roles.
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/artists/profile/role`,
        { role },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (patchUser) patchUser({ role: role === 'artist' ? 'artist' : 'user' });
    } catch (err) {
      console.log('Could not save role:', err);
    }

    setShowRoleSelection(false);

    if (role === 'artist') {
      navigate('/artist-setup');
    }
  };

  // Continuous station: when the stream auto-advances to another artist's track,
  // move the Live Room view to follow whoever is now playing.
  useEffect(() => {
    if (isSnippetMode || !currentTrack || rooms.length === 0) return;
    const artistId = currentTrack.artist_id || currentTrack.uploader_id;
    const idx = rooms.findIndex(r => r.id === artistId);
    if (idx !== -1 && idx !== currentRoomIndex) {
      setCurrentRoomIndex(idx);
    }
  }, [currentTrack, rooms, isSnippetMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch rooms/artists based on genre
  const fetchRooms = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const params = new URLSearchParams();
      if (selectedGenre !== 'all') params.append('genre', selectedGenre);
      params.append('limit', '500');
      params.append('full', 'true');
      
      // Try AI DJ first (full-catalog mode = every uploaded track), fall back to featured
      let tracks = [];
      try {
        const aiResponse = await axios.get(
          `${API_URL}/api/radio/algorithm/ai-dj?${params}`,
          { headers }
        );
        // AI DJ returns "playlist"; keep "tracks" as a defensive fallback
        tracks = aiResponse.data?.playlist || aiResponse.data?.tracks || [];
      } catch (e) {
        console.log('AI DJ not available, using featured tracks');
      }
      
      // Fallback to featured tracks if AI DJ returns empty
      if (tracks.length === 0) {
        const featuredResponse = await axios.get(
          `${API_URL}/api/artists/radio/featured?limit=500`,
          { headers }
        );
        tracks = featuredResponse.data?.tracks || [];
      }
      
      if (tracks.length > 0) {
        // Group tracks by artist to create "rooms"
        const artistRooms = {};
        tracks.forEach(track => {
          const artistId = track.artist_id || track.uploader_id;
          if (!artistRooms[artistId]) {
            artistRooms[artistId] = {
              id: artistId,
              artistName: track.artist_name || 'Unknown Artist',
              artistImage: track.artist_profile_picture || track.artwork,
              artistUserId: track.uploader_id || track.artist_id, // User ID for stories
              isLive: Math.random() > 0.7,
              tracks: [],
              listenerCount: Math.floor(Math.random() * 500) + 50,
              genre: track.genre || 'hip-hop'
            };
          }
          artistRooms[artistId].tracks.push(track);
        });
        
        setRooms(Object.values(artistRooms));
        // Keep the flat, cross-artist ordered list as the continuous "station" playlist
        setStationTracks(tracks);
      }
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedGenre]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  // Simulate listeners - arranged in a cleaner orbital pattern
  useEffect(() => {
    const generateListeners = () => {
      // Fewer listeners for cleaner look (4-8 instead of 5-17)
      const count = Math.floor(Math.random() * 5) + 4;
      
      // Position listeners in orbital zones around the center
      const zones = [
        { x: 15, y: 25 },   // Top-left
        { x: 85, y: 20 },   // Top-right
        { x: 10, y: 50 },   // Middle-left
        { x: 90, y: 55 },   // Middle-right
        { x: 20, y: 75 },   // Bottom-left
        { x: 80, y: 70 },   // Bottom-right
        { x: 25, y: 35 },   // Inner top-left
        { x: 75, y: 40 },   // Inner top-right
      ];
      
      const newListeners = Array.from({ length: Math.min(count, zones.length) }, (_, i) => ({
        id: `listener-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`,
        name: `User${Math.floor(Math.random() * 1000)}`,
        avatar: i % 3 === 0 ? `https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=150&q=80` : null,
        position: {
          x: zones[i].x + (Math.random() - 0.5) * 8,
          y: zones[i].y + (Math.random() - 0.5) * 8
        }
      }));
      setListeners(newListeners);
    };
    
    generateListeners();
    // Gentler movement
    const interval = setInterval(() => {
      setListeners(prev => prev.map(l => ({
        ...l,
        position: {
          x: Math.max(5, Math.min(95, l.position.x + (Math.random() - 0.5) * 1.5)),
          y: Math.max(15, Math.min(80, l.position.y + (Math.random() - 0.5) * 1.5))
        }
      })));
    }, 4000);
    
    return () => clearInterval(interval);
  }, [currentRoomIndex]);

  // Simulate chat
  useEffect(() => {
    const messages = [
      { id: `msg-init-1-${crypto.randomUUID()}`, user: 'DJ_Mike', text: 'This beat is fire!', listenerId: listeners[0]?.id },
      { id: `msg-init-2-${crypto.randomUUID()}`, user: 'MusicLover', text: 'Drop incoming?', listenerId: listeners[2]?.id },
    ];
    setChatMessages(messages);
    
    const interval = setInterval(() => {
      const randomMessages = [
        'Sheesh!', 'Hard!', 'Play that again', 'W artist', 
        'This slaps', 'Need this on repeat', 'Vibes', 'Fire!'
      ];
      const newMsg = {
        id: `msg-${Date.now()}-${crypto.randomUUID()}`,
        user: `User${Math.floor(Math.random() * 100)}`,
        text: randomMessages[Math.floor(Math.random() * randomMessages.length)],
        listenerId: listeners[Math.floor(Math.random() * listeners.length)]?.id
      };
      
      setChatMessages(prev => [...prev.slice(-10), newMsg]);
      setEnergyLevel(prev => Math.min(100, prev + 5));
    }, 4000 + Math.random() * 3000);
    
    return () => clearInterval(interval);
  }, [listeners.length]);

  // Energy decay
  useEffect(() => {
    const interval = setInterval(() => {
      setEnergyLevel(prev => Math.max(20, prev - 1));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Start a given room's track on the continuous station (used when swiping/tapping to a new artist)
  const playRoomTrack = useCallback((room) => {
    if (!room?.tracks?.length || isSnippetMode) return;
    const track = room.tracks[0];
    const list = stationTracks.length ? stationTracks : room.tracks;
    const idx = Math.max(0, list.findIndex(t => t.id === track.id));
    if (setRepeatMode) setRepeatMode('all');
    playTrack(track, list, idx);
  }, [isSnippetMode, stationTracks, setRepeatMode, playTrack]);

  // Handle vertical scroll/swipe
  const handleScroll = useCallback((direction) => {
    const now = Date.now();
    if (now - lastScrollTime.current < 500) return;
    lastScrollTime.current = now;

    let newIndex = null;
    if (direction === 'up' && currentRoomIndex > 0) newIndex = currentRoomIndex - 1;
    else if (direction === 'down' && currentRoomIndex < rooms.length - 1) newIndex = currentRoomIndex + 1;
    if (newIndex === null) return;

    setCurrentRoomIndex(newIndex);
    // If a song is already playing, swiping to the next artist plays that artist's song too.
    if (currentTrack) playRoomTrack(rooms[newIndex]);
  }, [currentRoomIndex, rooms, currentTrack, playRoomTrack]);

  const handleWheel = useCallback((e) => {
    if (Math.abs(e.deltaY) > 50) {
      handleScroll(e.deltaY > 0 ? 'down' : 'up');
    }
  }, [handleScroll]);

  const touchStartY = useRef(0);
  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };
  
  const handleTouchEnd = (e) => {
    const deltaY = touchStartY.current - e.changedTouches[0].clientY;
    if (Math.abs(deltaY) > 50) {
      handleScroll(deltaY > 0 ? 'down' : 'up');
    }
  };

  // Handle play - uses snippet mode for non-authenticated users.
  // Authenticated users get the continuous cross-artist "NLT Radio" station.
  const handlePlayRoom = (room) => {
    if (room.tracks.length === 0) return;
    const track = room.tracks[0];

    if (isSnippetMode) {
      // Non-authenticated: play 10-second snippet
      playSnippet(track);
      return;
    }

    // Build the station playlist (full cross-artist stream) and start at this artist.
    const list = stationTracks.length ? stationTracks : room.tracks;
    const idx = Math.max(0, list.findIndex(t => t.id === track.id));
    // Endless radio-station feel: loop the whole stream.
    if (setRepeatMode) setRepeatMode('all');
    playTrack(track, list, idx);
  };

  // Handle pause
  const handlePauseRoom = () => {
    if (isSnippetMode) {
      stopSnippet();
    } else {
      togglePlayPause();
    }
  };

  // Determine if currently playing this room
  const isRoomPlaying = (room) => {
    if (isSnippetMode) {
      return currentSnippetTrack?.artist_id === room.id || 
             currentSnippetTrack?.uploader_id === room.id;
    }
    return isPlaying && (currentTrack?.artist_id === room.id || currentTrack?.uploader_id === room.id);
  };

  const currentRoom = rooms[currentRoomIndex];

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#050505] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          className="w-16 h-16 border-2 border-[#00F0FF] border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <>
      {/* Sleek / Classic pill toggle — positioned just below the measured nav so it never overlaps */}
      <div
        className="fixed left-1/2 -translate-x-1/2 z-[60] flex items-center gap-0.5 p-0.5 rounded-full bg-black/70 backdrop-blur-md border border-white/10 shadow-lg"
        style={{ top: navHeight ? navHeight + 14 : 128 }}
        data-testid="view-mode-toggle"
      >
        <button
          onClick={() => changeViewMode('sleek')}
          data-testid="mode-toggle-sleek"
          className={cn(
            "px-3 py-1 rounded-full text-[11px] font-bold tracking-wide transition-colors",
            viewMode === 'sleek' ? "bg-white text-[#050505]" : "text-white/60 hover:text-white"
          )}
        >
          Sleek
        </button>
        <button
          onClick={() => changeViewMode('classic')}
          data-testid="mode-toggle-classic"
          className={cn(
            "px-3 py-1 rounded-full text-[11px] font-bold tracking-wide transition-colors",
            viewMode === 'classic' ? "bg-white text-[#050505]" : "text-white/60 hover:text-white"
          )}
        >
          Classic
        </button>
        <button
          onClick={() => changeViewMode('club')}
          data-testid="mode-toggle-club"
          className={cn(
            "px-3 py-1 rounded-full text-[11px] font-bold tracking-wide transition-colors",
            viewMode === 'club' ? "bg-white text-[#050505]" : "text-white/60 hover:text-white"
          )}
        >
          Club
        </button>
      </div>

      {viewMode === 'sleek' ? (
        <SleekHome
          genres={GENRES}
          selectedGenre={selectedGenre}
          setSelectedGenre={setSelectedGenre}
          navHeight={navHeight}
          user={user}
          isAuthenticated={isAuthenticated}
          rooms={rooms}
          currentRoomIndex={currentRoomIndex}
          setCurrentRoomIndex={setCurrentRoomIndex}
          currentTrack={isSnippetMode ? currentSnippetTrack : currentTrack}
          isPlaying={isPlaying}
          isSnippetMode={isSnippetMode}
          energyLevel={energyLevel}
          onPlayRoom={handlePlayRoom}
          onPauseRoom={handlePauseRoom}
          isRoomPlaying={isRoomPlaying}
          playRoomTrack={playRoomTrack}
          onViewStory={setShowStoryViewer}
        />
      ) : viewMode === 'club' ? (
        <div
          className="fixed inset-0 bg-[#040207] overflow-hidden select-none"
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          data-testid="club-home"
        >
          <TopNav
            genres={GENRES}
            selectedGenre={selectedGenre}
            onGenreChange={setSelectedGenre}
            user={user}
            isAuthenticated={isAuthenticated}
          />
          {/* Room indicators */}
          <div className="fixed right-4 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-2">
            {rooms.map((room, index) => (
              <button
                key={room.id}
                onClick={() => { setCurrentRoomIndex(index); if (currentTrack) playRoomTrack(rooms[index]); }}
                data-testid={`club-room-indicator-${index}`}
                className={cn(
                  "w-2 h-2 rounded-full transition-all duration-300",
                  index === currentRoomIndex ? "bg-[#FF007F] w-3 h-3 shadow-[0_0_10px_#FF007F]" : "bg-white/30 hover:bg-white/50"
                )}
              />
            ))}
          </div>
          <AnimatePresence mode="wait">
            {currentRoom && (
              <ClubRoom
                key={currentRoom.id}
                room={currentRoom}
                isPlaying={isRoomPlaying(currentRoom)}
                onPlay={() => handlePlayRoom(currentRoom)}
                onPause={handlePauseRoom}
                energyLevel={energyLevel}
                currentTrack={isSnippetMode ? currentSnippetTrack : currentTrack}
                navHeight={navHeight}
              />
            )}
          </AnimatePresence>
        </div>
      ) : (
        <div
          className="fixed inset-0 bg-[#050505] overflow-hidden select-none"
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          data-testid="home-page"
        >
          {/* Noise Texture */}
          <div 
            className="fixed inset-0 pointer-events-none opacity-[0.03] z-50"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
            }}
          />

          {/* Top Navigation */}
          <TopNav 
            genres={GENRES}
            selectedGenre={selectedGenre}
            onGenreChange={setSelectedGenre}
            user={user}
            isAuthenticated={isAuthenticated}
          />

          {/* Room Navigation Indicators */}
          <div className="fixed right-4 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-2">
            {rooms.map((room, index) => (
              <button
                key={room.id}
                onClick={() => { setCurrentRoomIndex(index); if (currentTrack) playRoomTrack(rooms[index]); }}
                data-testid={`room-indicator-${index}`}
                className={cn(
                  "w-2 h-2 rounded-full transition-all duration-300",
                  index === currentRoomIndex 
                    ? "bg-[#00F0FF] w-3 h-3 shadow-[0_0_10px_#00F0FF]" 
                    : "bg-white/30 hover:bg-white/50"
                )}
              />
            ))}
          </div>

          {/* Interface Mode Switcher */}
          <div className="fixed left-4 top-1/2 -translate-y-1/2 z-40 flex items-center gap-2">
            <button
              onClick={() => setShowInterfaceMenu(!showInterfaceMenu)}
              className="p-2 bg-black/40 backdrop-blur-sm rounded-full border border-white/10 hover:border-white/30 transition"
              data-testid="interface-switcher-btn"
              title="Change view"
            >
              <Settings2 className="w-5 h-5 text-white/70" />
            </button>
            
            {/* Interface Menu */}
            <AnimatePresence>
              {showInterfaceMenu && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="absolute left-12 top-0 bg-black/90 backdrop-blur-md rounded-xl border border-white/10 p-2 min-w-[180px]"
                >
                  <p className="text-xs text-gray-400 px-2 py-1 mb-1">Interface Mode</p>
                  {INTERFACE_MODES.map((mode) => {
                    const Icon = mode.icon;
                    return (
                      <button
                        key={mode.id}
                        onClick={() => handleInterfaceChange(mode.id)}
                        className={cn(
                          "w-full flex items-center gap-3 px-3 py-2 rounded-lg transition",
                          interfaceMode === mode.id 
                            ? "bg-purple-600/30 text-white" 
                            : "text-gray-300 hover:bg-white/5"
                        )}
                        data-testid={`interface-mode-${mode.id}`}
                      >
                        <Icon className="w-4 h-4" />
                        <div className="text-left">
                          <p className="text-sm font-medium">{mode.name}</p>
                          <p className="text-[10px] text-gray-500">{mode.desc}</p>
                        </div>
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Playlists quick-access */}
          <button
            onClick={() => setShowPlaylists(true)}
            className="fixed left-4 z-40 flex items-center justify-center p-2 bg-black/40 backdrop-blur-sm rounded-full border border-white/10 hover:border-[#00F0FF]/50 transition"
            style={{ top: 'calc(50% + 3rem)' }}
            data-testid="open-playlists-btn"
            title="Playlists"
          >
            <ListMusic className="w-5 h-5 text-[#00F0FF]" />
          </button>

          {/* Swipe Hints */}
          <AnimatePresence>
            {currentRoomIndex > 0 && (
              <motion.button
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                onClick={() => handleScroll('up')}
                className="fixed top-[168px] left-1/2 -translate-x-1/2 z-30 text-white/50 hover:text-white transition-colors"
                data-testid="swipe-up-hint"
              >
                <ChevronUp className="w-8 h-8 animate-bounce" />
              </motion.button>
            )}
            {currentRoomIndex < rooms.length - 1 && (
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                onClick={() => handleScroll('down')}
                className="fixed bottom-32 left-1/2 -translate-x-1/2 z-30 text-white/50 hover:text-white transition-colors"
                data-testid="swipe-down-hint"
              >
                <ChevronDown className="w-8 h-8 animate-bounce" />
              </motion.button>
            )}
          </AnimatePresence>

          {/* Live Rooms */}
          <div className="relative w-full h-full">
            <AnimatePresence mode="wait">
              {currentRoom && (
                <LiveRoom
                  key={currentRoom.id}
                  room={currentRoom}
                  isPlaying={isRoomPlaying(currentRoom)}
                  onPlay={() => handlePlayRoom(currentRoom)}
                  onPause={handlePauseRoom}
                  energyLevel={energyLevel}
                  listeners={interfaceMode === 'focus' ? [] : listeners}
                  chatMessages={interfaceMode === 'focus' ? [] : chatMessages}
                  currentTrack={isSnippetMode ? currentSnippetTrack : currentTrack}
                  isSnippetMode={isSnippetMode}
                  onViewStory={(storyUser) => setShowStoryViewer(storyUser)}
                  interfaceMode={interfaceMode}
                  navHeight={navHeight}
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* ===== Shared across both modes ===== */}

      {/* Genre-switch prompt: when a genre station runs out of tracks, ask the listener */}
      {queueEnded && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" data-testid="genre-switch-modal">
          <div className="w-full max-w-sm bg-[#141019] border border-white/10 rounded-2xl p-6">
            <h3 className="text-white text-lg font-bold">That's the end of this station</h3>
            <p className="text-[#A1A1AA] text-sm mt-1">
              {selectedGenre && selectedGenre !== 'all'
                ? `You've heard all the ${(GENRES.find(g => g.id === selectedGenre)?.name) || ''} tracks. Want to hear another genre?`
                : 'Want to keep listening? Pick a genre to keep the music going.'}
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              {GENRES.filter(g => g.id !== 'all' && g.id !== selectedGenre).map(g => (
                <button
                  key={g.id}
                  data-testid={`genre-switch-${g.id}`}
                  onClick={() => { setQueueEnded(false); setSelectedGenre(g.id); }}
                  className="px-4 py-2 rounded-full bg-white/5 hover:bg-white/15 text-white text-sm font-semibold transition-colors"
                >
                  {g.name}
                </button>
              ))}
            </div>
            <button
              onClick={() => setQueueEnded(false)}
              data-testid="genre-switch-dismiss"
              className="w-full mt-4 py-2.5 rounded-full border border-white/15 text-[#A1A1AA] text-sm hover:bg-white/5 transition-colors"
            >
              No thanks, stop here
            </button>
          </div>
        </div>
      )}

      {/* Snippet Timer Overlay */}
      <AnimatePresence>
        {isSnippetMode && currentSnippetTrack && snippetTimeLeft > 0 && (
          <SnippetOverlay
            timeLeft={snippetTimeLeft}
            totalTime={SNIPPET_DURATION}
            trackTitle={currentSnippetTrack.track_title}
            artistName={currentSnippetTrack.artist_name}
            navHeight={navHeight}
          />
        )}
      </AnimatePresence>

      {/* Signup Prompt - shows after snippet ends */}
      <SignupPrompt
        visible={showSignupPrompt}
        onClose={dismissSignupPrompt}
        artistName={currentSnippetTrack?.artist_name}
        trackTitle={currentSnippetTrack?.track_title}
        onSuccess={() => {
          if (currentSnippetTrack) {
            playTrack(currentSnippetTrack, currentRoom?.tracks || [currentSnippetTrack], 0);
          }
        }}
      />

      {/* Role Selection Modal - shows for new authenticated users */}
      <RoleSelectionModal
        visible={showRoleSelection}
        onSelect={handleRoleSelect}
        userName={user?.name}
      />

      {/* Story Viewer */}
      {showStoryViewer && (
        <StoryViewer 
          storyUser={showStoryViewer}
          onClose={() => setShowStoryViewer(null)}
        />
      )}

      {/* Story Creator */}
      {showStoryCreator && (
        <StoryCreator 
          onClose={() => setShowStoryCreator(false)}
          onSuccess={() => setShowStoryCreator(false)}
        />
      )}

      {/* Playlists Drawer */}
      <PlaylistsDrawer open={showPlaylists} onClose={() => setShowPlaylists(false)} />
    </>
  );
};

export default HomePage;
